//#region [IMPORT]

// === THIRD-PARTY ===
import { useCallback, useEffect, useState } from 'react';

// === HOOKS ===
import { useTaskStatus } from '../useTaskStatus/index';

// === UTILS ===
import { getProkeyUrl } from 'components/utils';

// === API ===
import { on, removeListener, sendMessageData } from 'api/prokey/prokey.websocket';

//#endregion

//-----------------------------------------------------------------
export function useTaskWindows() {
  //-----------------------------------------------------------------
  //#region [VALUES]

  const { has_task, views } = useTaskStatus();
  
  const [ fields_info , setFieldsInfo ] = useState( {} );
  const [ images_info , setImagesInfo ] = useState( {} );
  const [ thumbs_info , setThumbsInfo ] = useState( {} );

  const [ window_fields, setWindowFields ] = useState( null );
  const [ window_images, setWindowImages ] = useState( null );
  const [ window_thumbs, setWindowThumbs ] = useState( null );

  const screen_width  = window.screen.width ;
  const screen_height = window.screen.height;

  const prokey_url = getProkeyUrl();

  //#endregion

  //#region [PROCES]

  updateWindowFields();
  updateWindowImages();
  updateWindowThumbs();

  window.onpagehide = () => {
    closeWindowFields();
    closeWindowImages();
    closeWindowThumbs();
  }

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleFieldsInfo( data ) {
      //-----------------------------------------------------------------
      setFieldsInfo( data );
    };
    //-----------------------------------------------------------------
    function handleImagesInfo( data ) {
      //-----------------------------------------------------------------
      setImagesInfo( data );
    };
    //-----------------------------------------------------------------
    function handleThumbsInfo( data ) {
      //-----------------------------------------------------------------
      setThumbsInfo( data );
    };

    on( 'PROKEY_FIELDS_VIEW', handleFieldsInfo );
    on( 'PROKEY_IMAGES_VIEW', handleImagesInfo );
    on( 'PROKEY_THUMBS_VIEW', handleThumbsInfo );

    return () => {
      removeListener( 'PROKEY_FIELDS_VIEW', handleFieldsInfo );
      removeListener( 'PROKEY_IMAGES_VIEW', handleImagesInfo );
      removeListener( 'PROKEY_THUMBS_VIEW', handleThumbsInfo );
    }
  }, [] );

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function updateWindowFields() {
    //-----------------------------------------------------------------
    try {
      if( !fields_info.is_detached ) {
        closeWindowFields();
      }

      if( !window_fields && fields_info.is_detached ) {
        const position = fields_info.position || {};
        const height = position.height || screen_height-15;
        const width  = position.width  || parseInt(screen_width * 0.4);
        const left   = position.left   || 0;
        const top    = position.top    || 0;
        const fields = window.open( `${prokey_url}verify/fields` , 'prokey-fields', `toolbar=no,scrollbars=yes,resizable=yes,top=${top},left=${left},width=${width},height=${height}` )
        setWindowFields( fields );

        fields.onload = () => {
          fields.onbeforeunload = (event) => {
            closeWindowFields(fields);
          };
        }
      }
    } catch (error) {
      console.warn( error );
    }
  }

  //-----------------------------------------------------------------
  function updateWindowImages() {
    //-----------------------------------------------------------------
    try {
      if( !images_info.is_detached ) {
        closeWindowImages();
      }

      if( !window_images && images_info.is_detached ) {
        const position = images_info.position || {};
        const height = position.height || screen_height;
        const width  = position.width  || parseInt(screen_width * 0.5) - 15;
        const left   = position.left   || parseInt(screen_width * 0.5) + 15;
        const top    = position.top    || 0;
        const images = window.open( `${prokey_url}verify/image` , 'prokey-images', `toolbar=no,scrollbars=yes,resizable=yes,top=${top},left=${left},width=${width},height=${height}` )
        setWindowImages( images );

        images.onload = () => {
          images.onbeforeunload = () => {
            closeWindowImages(images);
          }
        }
      }
    } catch (error) {
      
    }
  }

  //-----------------------------------------------------------------
  function updateWindowThumbs() {
    //-----------------------------------------------------------------
    try {
      if( !thumbs_info.is_detached ) {
        closeWindowThumbs();
      }

      if( !window_thumbs && thumbs_info.is_detached ) {
        const position = thumbs_info.position || {};
        const height = position.height || screen_height;
        const width  = position.width  || parseInt(screen_width * 0.1);
        const left   = position.left   || parseInt(screen_width * 0.4);
        const top    = position.top    || 0;
        const thumbs = window.open( `${prokey_url}verify/thumbnails` , 'prokey-thumbs', `top=${top},left=${left},width=${width},height=${height}` )
        setWindowThumbs( thumbs );

        thumbs.onload = () => {
          thumbs.onbeforeunload = (event) => {
            closeWindowThumbs(thumbs);
          };
        }
      }
    } catch (error) {
      
    }
  }

  //-----------------------------------------------------------------
  function closeWindowFields(my_window = null) {
    //-----------------------------------------------------------------
    try {
      let position;
      const active_window = my_window || window_fields;
      if( active_window ) {
        position = {};
        position.left   = active_window.screenLeft  ;
        position.top    = active_window.screenTop   ;
        position.width  = active_window.outerWidth - 16; // opening a window adds 16 to the width for some reason
        position.height = active_window.outerHeight ;
      }
  
      if( window_fields ) {
        window_fields?.close();
        setWindowFields( null );
      }
  
      if( fields_info.is_detached ) {
        setFieldsInfo( {is_detached: false} )
        sendMessageData( 'PROKEY_FIELDS_VIEW', { is_detached: false, position } );
      }
    } catch (error) {
      console.warn( error );
    }
  }

  //-----------------------------------------------------------------
  function closeWindowImages(my_window = null) {
    //-----------------------------------------------------------------
    try {
      let position;
      const active_window = my_window || window_images;
      if( active_window ) {
        position = {};
        position.left   = active_window.screenLeft  ;
        position.top    = active_window.screenTop   ;
        position.width  = active_window.outerWidth - 16; // opening a window adds 16 to the width for some reason
        position.height = active_window.outerHeight ;
      }
  
      if( window_images ) {
        window_images?.close();
        setWindowImages( null );
      }
  
      if( images_info.is_detached ) {
        setImagesInfo( {is_detached: false} )
        sendMessageData( 'PROKEY_IMAGES_VIEW', { is_detached: false, position } );
      }
    } catch (error) {
      console.warn( error );
    }
  }

  //-----------------------------------------------------------------
  function closeWindowThumbs(my_window = null) {
    //-----------------------------------------------------------------
    try {
      let position;
      const active_window = my_window || window_thumbs;
      if( active_window ) {
        position = {};
        position.left   = active_window.screenLeft  ;
        position.top    = active_window.screenTop   ;
        position.width  = active_window.outerWidth - 16; // opening a window adds 16 to the width for some reason
        position.height = active_window.outerHeight ;
      }
  
      if( window_thumbs ) {
        window_thumbs?.close();
        setWindowThumbs( null );
      }
  
      if( thumbs_info.is_detached ) {
        setThumbsInfo( {is_detached: false} )
        sendMessageData( 'PROKEY_THUMBS_VIEW', { is_detached: false, position } );
      }
    } catch (error) {
      console.warn( error );
    }
  }

  //#endregion
}